import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';
import { db } from '../firebase';
import { useTranslation } from '../hooks/useTranslation';
import ReviewedHairstylistCard from '../components/ReviewedHairstylistCard';
import styles from './ProfilePage.module.css';

function ReviewsPage() {
  const { currentUser } = useAuth();
  const { language } = useTranslation();
  const navigate = useNavigate();
  const [reviewedData, setReviewedData] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Récupérer les reviews de l'utilisateur
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('userId', '==', currentUser.uid),
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        const reviewsData = reviewsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Récupérer les données des coiffeuses notées avec leurs reviews
        const dataWithReviews = await Promise.all(
          reviewsData.map(async (review) => {
            const stylistDoc = await getDoc(doc(db, 'hairstylists', review.hairstylistId));
            if (stylistDoc.exists()) {
              return {
                stylist: { id: stylistDoc.id, ...stylistDoc.data() },
                review: review,
              };
            }
            return null;
          }),
        );
        
        setReviewedData(dataWithReviews.filter(Boolean));

        // Récupérer les services pour le lookup
        const servicesSnap = await getDocs(query(collection(db, 'services'), where('type', '==', DEFAULT_PROVIDER_TYPE)));
        const servicesData = servicesSnap.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            code: data.code || data.prestationCode || '',
            label: data.label || data.prestation || '',
          };
        }).filter((s) => s.code && s.label);
        setServices(servicesData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, navigate]);

  const serviceLabelLookup = services.reduce((acc, service) => {
    // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
    let label = service.label || service.prestation || '';
    if (language && language !== 'fr' && service.en) {
      label = service.en;
    }
    acc[service.code] = label;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Chargement...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Mes coiffeuses notées</h1>
        {reviewedData.length > 0 ? (
          <div className={styles.reviewsList}>
            {reviewedData.map(({ stylist, review }) => (
              <ReviewedHairstylistCard
                key={review.id}
                stylist={stylist}
                review={review}
            serviceLookup={serviceLabelLookup}
          />
            ))}
          </div>
        ) : (
          <p className={styles.empty}>Aucune coiffeuse notée pour le moment</p>
        )}
      </div>
    </div>
  );
}

export default ReviewsPage;

